import { styleMap } from 'lit/directives/style-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref, createRef } from 'lit/directives/ref.js'
import { property, state } from 'lit/decorators.js'
import { html, nothing } from 'lit'

import { LfuiElement } from '#utils/element'
import { WithLang, compose } from '#utils/mixins'

import styles, { locals } from './lfds-spinner.css.ts'

const SPINNER_DELAY_MS = 800
const DELAYED_LABEL_DELAY_MS = 5000

const translations = {
	sv: {
		srLabel: 'Laddar innehåll, vänta lite',
		delayedLabel: 'Laddar fortfarande,\ntack för att du väntar'
	},
	en: {
		srLabel: 'Loading content, please wait',
		delayedLabel: 'Still loading,\nthank you for waiting'
	}
}

/**
 * @name LfdsSpinner
 *
 * @status beta
 * @tag lfds-spinner
 *
 * Indeterminate loading indicator with three variants:
 * - `plain` (default): spinner only; used inline or inside other components.
 * - `inline`: spinner with an optional delayed visible label below. Layout space
 *   is reserved from the start so the layout does not shift when the label appears.
 * - `overlay`: full-page modal overlay using a native `<dialog>`. Blocks all
 *   interaction and traps focus. Controlled with the `open` prop.
 *
 * Mirrors the Figma component's two background modes via `background-mode`:
 * - `on-light-background` (default): used inline or on a soft backdrop.
 * - `on-dark-background`: used on a strong backdrop. `overlay` selects this
 *   automatically since its default backdrop is strong; set `background-mode`
 *   explicitly to override (for example an `overlay` with a soft/light backdrop).
 *
 * Emits `lfds-overlay-closed` when the overlay closes. Event detail:
 * `{ focusRestored: boolean }`.
 *
 * ## Accessibility
 * - `plain` / `inline`: `role="progressbar"` with `sr-label` as accessible name.
 *   Add `aria-busy="true"` on the loading region in the consuming page.
 * - `overlay`: the `<dialog>` carries `sr-label` as its accessible name.
 *   The browser makes background content inert and traps focus automatically.
 *
 * @cssproperty --lfds-spinner-background - Background behind the spinner (default: transparent)
 * @cssproperty --lfds-spinner-overlay-backdrop - Overlay backdrop color (default: rgba(255,255,255,0.75))
 * @event lfds-overlay-closed - Emitted when overlay closes.
 */
export default class LfdsSpinner extends compose(LfuiElement, WithLang) {
	static styles = styles
	static events = ['lfds-overlay-closed']
	static readonly #globalAnnouncerId = 'lfds-spinner-global-announcer'

	readonly #dialogRef = createRef<HTMLDialogElement>()
	#spinnerVisibilityTimeout?: ReturnType<typeof setTimeout>
	#delayedLabelTimeout?: ReturnType<typeof setTimeout>
	#liveRegionTimeout?: ReturnType<typeof setTimeout>
	#globalAnnounceTimeout?: ReturnType<typeof setTimeout>
	#reduceMotionQuery?: MediaQueryList
	#lastFocusedBeforeOverlay?: HTMLElement

	@state() private showSpinner = false
	@state() private showDelayedLabel = false
	@state() private showLiveRegion = false
	@state() private liveRegionText = ''
	@state() private prefersReducedMotion = false

	/**
	 * Size presets:
	 * - `sm` = 40px
	 * - `md` = 48px (default)
	 * - `lg` = 64px
	 *
	 * Backward compatibility:
	 * legacy px values (`20|24|32|40|48|64`) are accepted and rendered at
	 * their exact pixel size.
	 */
	@property() size:
		| 'sm'
		| 'md'
		| 'lg'
		| 20
		| 24
		| 32
		| 40
		| 48
		| 64
		| '20'
		| '24'
		| '32'
		| '40'
		| '48'
		| '64' = 'md'

	/**
	 * Optional monochrome color override (for example white on dark overlays).
	 * Set to a CSS color value such as `currentcolor` or `#fff`.
	 */
	@property() color?: string

	/**
	 * Layout and behaviour variant.
	 * - `plain` (default): spinner only.
	 * - `inline`: spinner with an optional delayed label below.
	 * - `overlay`: full-page blocking overlay.
	 */
	@property({ reflect: true }) variant: 'plain' | 'inline' | 'overlay' = 'plain'

	/**
	 * Background context the spinner is placed on, matching the Figma
	 * component's `on-light-background` / `on-dark-background` modes.
	 * Defaults to `on-light-background`, except for `overlay`, which defaults
	 * to `on-dark-background` since its backdrop is strong by default. Set
	 * explicitly to override the automatic default.
	 */
	@property({ attribute: 'background-mode', reflect: true })
	backgroundMode?: 'on-light-background' | 'on-dark-background'

	/**
	 * Accessible name for assistive technology.
	 * Defaults to a localized built-in text if omitted.
	 */
	@property({ attribute: 'sr-label' }) srLabel = ''

	/**
	 * Visible status text shown after 5 seconds.
	 * Only rendered in `inline` and `overlay` variants.
	 * Defaults to a localized built-in text if omitted.
	 */
	@property({ attribute: 'delayed-label' }) delayedLabel = ''

	/**
	 * Opt-in to show spinner immediately instead of waiting 800 ms.
	 */
	@property({ type: Boolean, attribute: 'show-spinner-immediately' })
	showSpinnerImmediately = false

	/**
	 * Opt-in to show delayed label immediately instead of waiting 5000 ms.
	 */
	@property({ type: Boolean, attribute: 'show-label-immediately' })
	showLabelImmediately = false

	/**
	 * Controls visibility of the `overlay` variant.
	 * Ignored for `plain` and `inline`.
	 */
	@property({ type: Boolean }) open = false

	override connectedCallback() {
		super.connectedCallback()
		this.#setupReduceMotionPreference()
		this.#scheduleVisibility()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.#clearSpinnerTimer()
		this.#clearDelayedLabelTimer()
		this.#clearLiveRegionTimer()
		this.#clearGlobalAnnounceTimer()
		this.#teardownReduceMotionPreference()
		this.#setPageBusy(false)
	}

	override updated(changedProperties: Map<PropertyKey, unknown>) {
		if (
			changedProperties.has('delayedLabel') ||
			changedProperties.has('showSpinnerImmediately') ||
			changedProperties.has('showLabelImmediately')
		) {
			this.#scheduleVisibility()
		}

		if (this.variant === 'overlay' && changedProperties.has('open')) {
			if (this.open) {
				this.#scheduleVisibility()
			} else {
				this.#clearSpinnerTimer()
				this.#clearDelayedLabelTimer()
				this.showSpinner = false
				this.showDelayedLabel = false
			}
		}

		if (changedProperties.has('variant') && this.variant !== 'overlay') {
			this.#scheduleVisibility()
		}

		if (
			changedProperties.has('open') ||
			changedProperties.has('variant') ||
			changedProperties.has('showSpinner') ||
			changedProperties.has('showSpinnerImmediately')
		) {
			this.#syncDialogState()
		}
	}

	override firstUpdated() {
		// Ensure initial `open` state is applied once the dialog ref is available.
		this.#syncDialogState()
	}

	#syncDialogState() {
		const dialog = this.#dialogRef.value

		if (!dialog) {
			return
		}

		if (this.variant === 'overlay') {
			const shouldShowOverlay =
				this.open && (this.showSpinnerImmediately || this.showSpinner)
			this.#setPageBusy(shouldShowOverlay)

			if (shouldShowOverlay && !dialog.open) {
				this.#rememberFocusBeforeOverlayOpen()
				dialog.showModal()
			} else if (!shouldShowOverlay && dialog.open) {
				dialog.close()
			}
			return
		}

		this.#setPageBusy(false)

		if (dialog.open) {
			dialog.close()
		}
	}

	#clearSpinnerTimer() {
		if (!this.#spinnerVisibilityTimeout) {
			return
		}

		clearTimeout(this.#spinnerVisibilityTimeout)
		this.#spinnerVisibilityTimeout = undefined
	}

	#clearDelayedLabelTimer() {
		if (!this.#delayedLabelTimeout) {
			return
		}

		clearTimeout(this.#delayedLabelTimeout)
		this.#delayedLabelTimeout = undefined
	}

	#clearLiveRegionTimer() {
		if (!this.#liveRegionTimeout) {
			return
		}

		clearTimeout(this.#liveRegionTimeout)
		this.#liveRegionTimeout = undefined
	}

	#clearGlobalAnnounceTimer() {
		if (!this.#globalAnnounceTimeout) {
			return
		}

		clearTimeout(this.#globalAnnounceTimeout)
		this.#globalAnnounceTimeout = undefined
	}

	#isLoadingActive() {
		return this.variant === 'overlay' ? this.open : true
	}

	#scheduleVisibility() {
		this.#clearSpinnerTimer()
		this.#clearDelayedLabelTimer()
		this.#clearLiveRegionTimer()

		if (!this.#isLoadingActive()) {
			this.showSpinner = false
			this.showDelayedLabel = false
			this.showLiveRegion = false
			this.liveRegionText = ''
			return
		}

		this.showLiveRegion = false
		this.liveRegionText = ''
		const resolvedSrLabel = this.#resolvedSrLabel()
		const resolvedDelayedLabel = this.#resolvedDelayedLabel()
		const liveRegionDelayMs = this.showSpinnerImmediately ? 0 : SPINNER_DELAY_MS
		this.#liveRegionTimeout = setTimeout(() => {
			this.showLiveRegion = true
			this.liveRegionText = resolvedSrLabel
			this.#announcePolite(resolvedSrLabel)
			this.#liveRegionTimeout = undefined
		}, liveRegionDelayMs)

		if (this.showSpinnerImmediately) {
			this.showSpinner = true
		} else {
			this.showSpinner = false
			this.#spinnerVisibilityTimeout = setTimeout(() => {
				this.showSpinner = true
				this.#spinnerVisibilityTimeout = undefined
			}, SPINNER_DELAY_MS)
		}

		this.showDelayedLabel = false

		if (!resolvedDelayedLabel?.trim()) {
			return
		}

		if (this.showLabelImmediately) {
			this.showDelayedLabel = true
			if (this.showLiveRegion && resolvedDelayedLabel?.trim()) {
				this.liveRegionText = resolvedDelayedLabel
				this.#announcePolite(resolvedDelayedLabel)
			}
			return
		}

		this.#delayedLabelTimeout = setTimeout(() => {
			this.showDelayedLabel = true
			if (this.showLiveRegion && resolvedDelayedLabel?.trim()) {
				this.liveRegionText = resolvedDelayedLabel
				this.#announcePolite(resolvedDelayedLabel)
			}
			this.#delayedLabelTimeout = undefined
		}, DELAYED_LABEL_DELAY_MS)
	}

	#resolvedSrLabel() {
		return this.srLabel?.trim() || this.t(translations, 'srLabel')
	}

	#resolvedDelayedLabel() {
		return this.delayedLabel?.trim() || this.t(translations, 'delayedLabel')
	}

	#ensureGlobalAnnouncer() {
		const root = document?.body
		if (!root) {
			return undefined
		}

		const existing = document.getElementById(LfdsSpinner.#globalAnnouncerId)
		if (existing) {
			return existing
		}

		const announcer = document.createElement('div')
		announcer.id = LfdsSpinner.#globalAnnouncerId
		announcer.setAttribute('role', 'status')
		announcer.setAttribute('aria-live', 'polite')
		announcer.setAttribute('aria-atomic', 'true')
		announcer.style.position = 'absolute'
		announcer.style.inlineSize = '1px'
		announcer.style.blockSize = '1px'
		announcer.style.padding = '0'
		announcer.style.margin = '-1px'
		announcer.style.overflow = 'hidden'
		announcer.style.clip = 'rect(0 0 0 0)'
		announcer.style.clipPath = 'inset(50%)'
		announcer.style.whiteSpace = 'nowrap'
		announcer.style.border = '0'

		root.appendChild(announcer)
		return announcer
	}

	#announcePolite(message: string) {
		if (!message?.trim()) {
			return
		}

		const announcer = this.#ensureGlobalAnnouncer()
		if (!announcer) {
			return
		}

		this.#clearGlobalAnnounceTimer()
		announcer.textContent = ''
		this.#globalAnnounceTimeout = setTimeout(() => {
			announcer.textContent = message
			this.#globalAnnounceTimeout = undefined
		}, 0)
	}

	#setupReduceMotionPreference() {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return
		}

		this.#reduceMotionQuery = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		)
		this.prefersReducedMotion = this.#reduceMotionQuery.matches

		if (this.#reduceMotionQuery.addEventListener) {
			this.#reduceMotionQuery.addEventListener(
				'change',
				this.#handleReduceMotionChange
			)
		} else {
			this.#reduceMotionQuery.addListener(this.#handleReduceMotionChange)
		}
	}

	#teardownReduceMotionPreference() {
		if (!this.#reduceMotionQuery) {
			return
		}

		if (this.#reduceMotionQuery.removeEventListener) {
			this.#reduceMotionQuery.removeEventListener(
				'change',
				this.#handleReduceMotionChange
			)
		} else {
			this.#reduceMotionQuery.removeListener(this.#handleReduceMotionChange)
		}

		this.#reduceMotionQuery = undefined
	}

	#handleReduceMotionChange = (event: MediaQueryListEvent | MediaQueryList) => {
		this.prefersReducedMotion = event.matches
		this.requestUpdate()
	}

	#setPageBusy(isBusy: boolean) {
		const page = document?.body
		if (!page) {
			return
		}

		if (isBusy) {
			page.setAttribute('aria-busy', 'true')
			return
		}

		page.removeAttribute('aria-busy')
	}

	#rememberFocusBeforeOverlayOpen() {
		const active = document.activeElement
		this.#lastFocusedBeforeOverlay =
			active instanceof HTMLElement ? active : undefined
	}

	#restoreFocusAfterOverlayClose() {
		const target = this.#lastFocusedBeforeOverlay
		this.#lastFocusedBeforeOverlay = undefined

		if (!target || !target.isConnected) {
			return false
		}

		target.focus()
		return true
	}

	#handleOverlayClose = () => {
		this.#setPageBusy(false)

		if (this.open) {
			this.open = false
		}

		const focusRestored = this.#restoreFocusAfterOverlayClose()
		this.dispatchEvent(
			new CustomEvent('lfds-overlay-closed', {
				bubbles: true,
				composed: true,
				detail: {
					focusRestored
				}
			})
		)
	}

	#resolvedSize() {
		switch (this.size) {
			case 'lg':
			case 64:
			case '64':
				return 64
			case 'md':
			case 48:
			case '48':
				return 48
			case 'sm':
			case 40:
			case '40':
				return 40
			// Legacy px values render at exact pixel sizes
			case 32:
			case '32':
				return 32
			case 24:
			case '24':
				return 24
			case 20:
			case '20':
				return 20
			default:
				return 48
		}
	}

	#spinnerSpan(ariaHidden: boolean) {
		const srLabel = this.#resolvedSrLabel()

		return html`
			<span
				class="spinner ${this.color ? 'monochrome' : ''}"
				role=${ifDefined(ariaHidden ? undefined : 'progressbar')}
				aria-label=${ifDefined(ariaHidden ? undefined : srLabel)}
				?aria-hidden=${ariaHidden}
				style=${styleMap({
					[locals.internal_size]: String(this.#resolvedSize()),
					[locals.single_color]: this.color
				})}
			>
				<svg viewBox="0 0 72 72" fill="none" aria-hidden="true">
					<circle
						class="track"
						cx="36"
						cy="36"
						r="32"
						stroke-width="6"
					></circle>
					<path
						class="arc-path"
						d="M68 36C68 41.0499 66.8048 46.0282 64.5122 50.5277C62.2196 55.0272 58.8946 58.9203 54.8091 61.8885C50.7236 64.8568 45.9937 66.816 41.0059 67.606C36.0181 68.396 30.9142 67.9943 26.1115 66.4338C21.3087 64.8733 16.9434 62.1983 13.37258 58.6274C9.80174 55.0566 7.12671 50.6913 5.56619 45.8885C4.00567598 41.0858 3.60399 35.9819 4.393974 30.9941C5.18396 26.0063 7.14318 21.2764 10.11146 17.1909"
						pathLength="1"
						stroke-width="6.6"
						stroke-linecap="round"
					></path>
				</svg>
			</span>
		`
	}

	#spinnerPlaceholder() {
		return html`
			<span
				class="spinner placeholder"
				aria-hidden="true"
				style=${styleMap({
					[locals.internal_size]: String(this.#resolvedSize())
				})}
			></span>
		`
	}

	#labelDiv() {
		const label = this.#currentVisibleLabel()

		if (!label) {
			return nothing
		}

		return html`
			<div class="label ${this.#labelSizeClass()}">
				<span class=${this.showDelayedLabel ? 'delayed-label' : 'current-label'}
					>${label}</span
				>
			</div>
		`
	}

	#labelSizeClass() {
		switch (this.size) {
			case 'lg':
			case 64:
			case '64':
				return 'label-lg'
			// Legacy px values below `md` fall back to the `sm` label style
			case 'sm':
			case 40:
			case '40':
			case 32:
			case '32':
			case 24:
			case '24':
			case 20:
			case '20':
				return 'label-sm'
			default:
				return 'label-md'
		}
	}

	#currentVisibleLabel() {
		if (this.showDelayedLabel) {
			return this.#resolvedDelayedLabel()
		}

		if (this.prefersReducedMotion) {
			return this.#resolvedSrLabel()
		}

		return undefined
	}

	#liveRegion() {
		if (!this.#isLoadingActive()) {
			return nothing
		}

		return html`
			<span class="live-region" aria-hidden="true">
				${this.showLiveRegion ? this.liveRegionText : ''}
			</span>
		`
	}

	render() {
		const srLabel = this.#resolvedSrLabel()

		if (this.variant === 'overlay') {
			return html`
				<dialog
					class="overlay"
					aria-label=${srLabel}
					@close=${this.#handleOverlayClose}
					${ref(this.#dialogRef)}
				>
					${
						this.showSpinner
							? html`
									<div class="overlay-inner">
										${this.#spinnerSpan(true)} ${this.#labelDiv()}
										${this.#liveRegion()}
									</div>
								`
							: nothing
					}
				</dialog>
			`
		}

		if (this.variant === 'inline') {
			return html`
				<div
					class="root"
					role=${ifDefined(this.showSpinner ? 'progressbar' : undefined)}
					aria-label=${ifDefined(this.showSpinner ? srLabel : undefined)}
				>
					${this.showSpinner ? this.#spinnerSpan(true) : this.#spinnerPlaceholder()}
					${this.#labelDiv()} ${this.#liveRegion()}
				</div>
			`
		}

		// plain (default)
		if (!this.showSpinner) {
			return nothing
		}

		if (this.prefersReducedMotion) {
			return html`
				<div class="plain-reduced" role="progressbar" aria-label=${srLabel}>
					${this.#spinnerSpan(true)} ${this.#labelDiv()} ${this.#liveRegion()}
				</div>
			`
		}

		return html`${this.#spinnerSpan(false)}${this.#liveRegion()}`
	}
}
